import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useHistory } from "react-router-dom";
import { TbArrowLeft, TbArrowRight, TbCheck } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useToast } from "../../../../Hooks/toast";
import { createBaasCardProgram } from "../../../../Services/baasCardProgramsApi";
import { fetchBaasAccounts } from "../../../../Services/baasAccountsListApi";
import { fetchIssuerFunding } from "../../../../Services/baasIssuerFundingApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import {
  DEFAULT_CARD_PROGRAM_CONFIG,
  DEFAULT_PROCESSOR_BRAND,
  DEFAULT_PROCESSOR_ROUTE_LABEL,
  DEFAULT_PRODUCT_DESIGN,
  DEFAULT_INVOICE_DAY_OPTIONS,
  DEBIT_CREDIT_GUARANTEE_PERCENT,
  DEBIT_CREDIT_SETTLEMENT_LAGS,
  formatSettlementLags,
  isCobrandedProgramKind,
  isMastercardBlackVariant,
  PROGRAM_KIND_COBRANDED,
  PROGRAM_KIND_NATIVE,
  FUNDING_MODE_PIX_KEY,
  FUNDING_MODE_LINKED_ACCOUNT,
  ISSUANCE_ENTITY_PF,
  ISSUANCE_ENTITY_PJ,
  ISSUANCE_ENTITY_BOTH,
} from "./cardProgramDesignConstants";
import { BaasCardProgramDesignForm } from "./components/BaasCardProgramDesignForm";
import { CardDesignPreview } from "./components/CardDesignPreview";

const CARTOES_LIST_PATH = "/baas/cartoes";
export const CARTOES_CREATE_PATH = "/baas/cartoes/novo";

const CREATE_STEPS = ["design", "config", "review"];

const DEFAULT_CONFIG = { ...DEFAULT_CARD_PROGRAM_CONFIG };

const formatInvoiceDay = (day) => String(day).padStart(2, "0");

const toggleInvoiceDay = (currentDays, day) => {
  const normalized = currentDays ?? [];
  return normalized.includes(day)
    ? normalized.filter((item) => item !== day)
    : [...normalized, day].sort((a, b) => a - b);
};

const FundingModeStep = ({ config, onChange, accounts, pixKeyFundingEnabled = false }) => {
  const updateConfig = (patch) => onChange({ ...config, ...patch });
  const mode = pixKeyFundingEnabled
    ? config.fundingMode || FUNDING_MODE_LINKED_ACCOUNT
    : FUNDING_MODE_LINKED_ACCOUNT;
  const isLinked = mode === FUNDING_MODE_LINKED_ACCOUNT;
  const fundingOptions = [
    ...(pixKeyFundingEnabled
      ? [{ value: FUNDING_MODE_PIX_KEY, labelKey: "baas.cartoes.create.config.fundingModePixKey" }]
      : []),
    {
      value: FUNDING_MODE_LINKED_ACCOUNT,
      labelKey: "baas.cartoes.create.config.fundingModeLinkedAccount",
    },
  ];

  return (
    <div className="block text-sm sm:col-span-2">
      <span className="mb-2 block text-gray-600 dark:text-gray-400">
        {intl.get("baas.cartoes.create.config.fundingMode")}
      </span>
      <div className="flex flex-wrap gap-2">
        {fundingOptions.map((option) => {
          const selected = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() =>
                updateConfig({
                  fundingMode: option.value,
                  fundingAccountId:
                    option.value === FUNDING_MODE_LINKED_ACCOUNT ? config.fundingAccountId : "",
                })
              }
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                selected
                  ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                  : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-400"
              }`}
            >
              {intl.get(option.labelKey)}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {intl.get(
          !pixKeyFundingEnabled
            ? "baas.cartoes.create.config.fundingModePixKeyDisabled"
            : isLinked
              ? "baas.cartoes.create.config.fundingModeLinkedAccountHint"
              : "baas.cartoes.create.config.fundingModePixKeyHint",
        )}
      </p>
      {isLinked ? (
        <label className="mt-3 block text-sm">
          <span className="mb-1 block text-gray-600 dark:text-gray-400">
            {intl.get("baas.cartoes.create.config.fundingAccount")}
          </span>
          <select
            className={inputClass}
            value={config.fundingAccountId || ""}
            onChange={(event) => updateConfig({ fundingAccountId: event.target.value })}
          >
            <option value="">
              {intl.get("baas.cartoes.create.config.fundingAccountPlaceholder")}
            </option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name || account.alias || account.id}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
};

const ConfigStep = ({
  design,
  config,
  onChange,
  accounts,
  pixKeyFundingEnabled = false,
  issuancePolicy = ISSUANCE_ENTITY_BOTH,
}) => {
  const updateConfig = (patch) => onChange({ ...config, ...patch });
  const isBlackProduct = isMastercardBlackVariant(design);
  const isCobranded = isCobrandedProgramKind(config.programKind);
  const issuanceOptions =
    issuancePolicy === ISSUANCE_ENTITY_PF
      ? [{ value: ISSUANCE_ENTITY_PF, labelKey: "baas.cartoes.create.config.issuancePf" }]
      : issuancePolicy === ISSUANCE_ENTITY_PJ
        ? [{ value: ISSUANCE_ENTITY_PJ, labelKey: "baas.cartoes.create.config.issuancePj" }]
        : [
            { value: ISSUANCE_ENTITY_PF, labelKey: "baas.cartoes.create.config.issuancePf" },
            { value: ISSUANCE_ENTITY_PJ, labelKey: "baas.cartoes.create.config.issuancePj" },
          ];

  return (
    <div className="grid gap-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface sm:grid-cols-2">
      <FundingModeStep
        config={config}
        onChange={onChange}
        accounts={accounts}
        pixKeyFundingEnabled={pixKeyFundingEnabled}
      />
      <div className="block text-sm sm:col-span-2">
        <span className="mb-2 block text-gray-600 dark:text-gray-400">
          {intl.get("baas.cartoes.create.config.issuanceEntityType")}
        </span>
        <div className="flex flex-wrap gap-2">
          {issuanceOptions.map((option) => {
            const selected =
              (config.issuanceEntityType || ISSUANCE_ENTITY_PF) === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`rounded-lg border px-3 py-2 text-xs transition ${
                  selected
                    ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                    : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                }`}
                onClick={() => updateConfig({ issuanceEntityType: option.value })}
              >
                {intl.get(option.labelKey)}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.cartoes.create.config.issuanceEntityTypeHint")}
        </p>
      </div>
      <div className="block text-sm sm:col-span-2">
        <span className="mb-2 block text-gray-600 dark:text-gray-400">
          {intl.get("baas.cartoes.create.config.programKind")}
        </span>
        <div className="flex flex-wrap gap-2">
          {[
            { value: PROGRAM_KIND_NATIVE, labelKey: "baas.cartoes.create.config.programKindNative" },
            { value: PROGRAM_KIND_COBRANDED, labelKey: "baas.cartoes.create.config.programKindCobranded" },
          ].map((option) => {
            const selected = (config.programKind || PROGRAM_KIND_NATIVE) === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => updateConfig({ programKind: option.value })}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  selected
                    ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                    : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-400"
                }`}
              >
                {intl.get(option.labelKey)}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get(
            isCobranded
              ? "baas.cartoes.create.config.programKindCobrandedHint"
              : "baas.cartoes.create.config.programKindNativeHint",
          )}
        </p>
      </div>

      <div className="block text-sm sm:col-span-2">
        <span className="mb-1 block text-gray-600 dark:text-gray-400">
          {intl.get("baas.cartoes.create.config.processor")}
        </span>
        <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm font-medium text-brand-dark dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100">
          {intl.get("baas.cartoes.create.config.processorRouteSummary", {
            brand: DEFAULT_PROCESSOR_BRAND,
            route: DEFAULT_PROCESSOR_ROUTE_LABEL,
          })}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.cartoes.create.config.processorHint")}
        </p>
      </div>

      {isCobranded ? (
        <>
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.authorizerClientUrl")}
            </span>
            <input
              type="url"
              className={inputClass}
              placeholder={intl.get("baas.cartoes.create.config.authorizerClientUrlPlaceholder")}
              value={config.authorizerClientUrl ?? ""}
              onChange={(event) => updateConfig({ authorizerClientUrl: event.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.authorizerClientUrlHint")}
            </p>
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.authorizerPublicKey")}
            </span>
            <textarea
              className="min-h-[96px] w-full resize-y rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs leading-relaxed text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
              placeholder={intl.get("baas.cartoes.create.config.authorizerPublicKeyPlaceholder")}
              value={config.authorizerPublicKey ?? ""}
              onChange={(event) => updateConfig({ authorizerPublicKey: event.target.value })}
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.authorizerPublicKeyHint")}
            </p>
          </label>
        </>
      ) : null}

      {isBlackProduct ? (
        <>
          <div className="block text-sm sm:col-span-2">
            <span className="mb-2 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.invoiceDays")}
            </span>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_INVOICE_DAY_OPTIONS.map((day) => {
                const selected = (config.invoiceDays ?? []).includes(day);

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => updateConfig({ invoiceDays: toggleInvoiceDay(config.invoiceDays, day) })}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      selected
                        ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                        : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-400"
                    }`}
                  >
                    {formatInvoiceDay(day)}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.invoiceDaysHint")}
            </p>
          </div>

          <div className="block text-sm sm:col-span-2">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.settlementDays")}
            </span>
            <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm font-medium text-brand-dark dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100">
              {formatSettlementLags(DEBIT_CREDIT_SETTLEMENT_LAGS)}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.settlementDaysFixedHint")}
            </p>
          </div>

          <div className="block text-sm sm:col-span-2">
            <span className="mb-1 block text-gray-600 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.guaranteePercent")}
            </span>
            <p className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm font-medium text-brand-dark dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100">
              {DEBIT_CREDIT_GUARANTEE_PERCENT}%
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.guaranteePercentFixedHint")}
            </p>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 sm:col-span-2">
          {intl.get("baas.cartoes.create.config.prepaidVoucherHint")}
        </p>
      )}

      <label className="block text-sm sm:col-span-2">
        <span className="mb-1 block text-gray-600 dark:text-gray-400">
          {intl.get("baas.settings.webhookUrl")}
        </span>
        <input
          type="url"
          className={inputClass}
          placeholder={intl.get("baas.settings.webhookUrlPlaceholder")}
          value={config.webhookUrl ?? ""}
          onChange={(event) => updateConfig({ webhookUrl: event.target.value })}
        />
      </label>
    </div>
  );
};

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const StepTabs = ({ activeIndex, onStepClick }) => (
  <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4 dark:border-brand-dark-border">
    {CREATE_STEPS.map((step, index) => {
      const isActive = index === activeIndex;
      const isComplete = index < activeIndex;

      return (
        <button
          key={step}
          type="button"
          onClick={() => onStepClick(index)}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition ${
            isActive
              ? "bg-brand-dark text-white dark:bg-gray-100 dark:text-brand-dark"
              : isComplete
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                : "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400"
          }`}
        >
          {isComplete ? <TbCheck className="h-3.5 w-3.5" /> : null}
          {intl.get(`baas.cartoes.create.step.${step}`)}
        </button>
      );
    })}
  </div>
);

const ReviewStep = ({ design, config, accounts = [] }) => {
  const isBlackProduct = isMastercardBlackVariant(design);
  const isCobranded = isCobrandedProgramKind(config.programKind);
  const fundingMode = config.fundingMode || FUNDING_MODE_LINKED_ACCOUNT;
  const fundingAccount = accounts.find((account) => account.id === config.fundingAccountId);
  const invoiceDaysLabel = (config.invoiceDays ?? [])
    .map((day) => formatInvoiceDay(day))
    .join(", ");

  return (
  <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-start">
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
      <h3 className="dashboard-section-label">
        {intl.get("baas.cartoes.create.review.summary")}
      </h3>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">{intl.get("baas.cartoes.design.programName")}</dt>
          <dd className="font-medium text-brand-dark dark:text-gray-100">{design.name || "—"}</dd>
        </div>
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">{intl.get("baas.cartoes.design.network")}</dt>
          <dd className="font-medium capitalize text-brand-dark dark:text-gray-100">{design.network}</dd>
        </div>
        {design.network === "mastercard" ? (
          <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
            <dt className="text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.design.mastercardVariant.label")}
            </dt>
            <dd className="font-medium capitalize text-brand-dark dark:text-gray-100">
              {intl.get(`baas.cartoes.design.mastercardVariant.${design.mastercardVariant ?? "black"}`)}
            </dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">{intl.get("baas.cartoes.design.productType.label")}</dt>
          <dd className="font-medium text-brand-dark dark:text-gray-100">
            {intl.get(`baas.cartoes.design.productType.${design.productType ?? "debit_credit"}`)}
          </dd>
        </div>
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.create.config.fundingMode")}
          </dt>
          <dd className="font-medium text-brand-dark dark:text-gray-100">
            {intl.get(
              fundingMode === FUNDING_MODE_LINKED_ACCOUNT
                ? "baas.cartoes.create.config.fundingModeLinkedAccount"
                : "baas.cartoes.create.config.fundingModePixKey",
            )}
          </dd>
        </div>
        {fundingMode === FUNDING_MODE_LINKED_ACCOUNT ? (
          <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
            <dt className="text-gray-500 dark:text-gray-400">
              {intl.get("baas.cartoes.create.config.fundingAccount")}
            </dt>
            <dd className="max-w-[50%] truncate font-medium text-brand-dark dark:text-gray-100">
              {fundingAccount?.name || fundingAccount?.alias || config.fundingAccountId || "—"}
            </dd>
          </div>
        ) : null}
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.create.config.issuanceEntityType")}
          </dt>
          <dd className="font-medium text-brand-dark dark:text-gray-100">
            {intl.get(
              (config.issuanceEntityType || ISSUANCE_ENTITY_PF) === ISSUANCE_ENTITY_PJ
                ? "baas.cartoes.create.config.issuancePj"
                : "baas.cartoes.create.config.issuancePf",
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.create.config.programKind")}
          </dt>
          <dd className="font-medium text-brand-dark dark:text-gray-100">
            {intl.get(
              isCobranded
                ? "baas.cartoes.create.config.programKindCobranded"
                : "baas.cartoes.create.config.programKindNative",
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.create.config.processor")}
          </dt>
          <dd className="font-medium text-brand-dark dark:text-gray-100">
            {intl.get("baas.cartoes.create.config.processorRouteSummary", {
              brand: DEFAULT_PROCESSOR_BRAND,
              route: DEFAULT_PROCESSOR_ROUTE_LABEL,
            })}
          </dd>
        </div>
        {isCobranded ? (
          <>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
              <dt className="text-gray-500 dark:text-gray-400">
                {intl.get("baas.cartoes.create.config.authorizerClientUrl")}
              </dt>
              <dd className="max-w-[50%] truncate font-medium text-brand-dark dark:text-gray-100">
                {config.authorizerClientUrl || "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
              <dt className="text-gray-500 dark:text-gray-400">
                {intl.get("baas.cartoes.create.config.authorizerPublicKey")}
              </dt>
              <dd className="max-w-[50%] truncate font-mono text-xs font-medium text-brand-dark dark:text-gray-100">
                {config.authorizerPublicKey?.trim() ? intl.get("baas.cartoes.create.config.authorizerPublicKeyConfigured") : "—"}
              </dd>
            </div>
          </>
        ) : null}
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
          <dt className="text-gray-500 dark:text-gray-400">
            {intl.get("baas.settings.webhookUrl")}
          </dt>
          <dd className="max-w-[50%] truncate font-medium text-brand-dark dark:text-gray-100">
            {config.webhookUrl?.trim() || "—"}
          </dd>
        </div>
        {isBlackProduct ? (
          <>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
              <dt className="text-gray-500 dark:text-gray-400">
                {intl.get("baas.cartoes.create.config.invoiceDays")}
              </dt>
              <dd className="font-medium text-brand-dark dark:text-gray-100">
                {invoiceDaysLabel || "—"}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
              <dt className="text-gray-500 dark:text-gray-400">
                {intl.get("baas.cartoes.create.config.settlementDays")}
              </dt>
              <dd className="font-medium text-brand-dark dark:text-gray-100">
                {formatSettlementLags(DEBIT_CREDIT_SETTLEMENT_LAGS)}
              </dd>
            </div>
            <div className="flex justify-between gap-4 border-b border-gray-100 py-2 dark:border-brand-dark-border">
              <dt className="text-gray-500 dark:text-gray-400">
                {intl.get("baas.cartoes.create.config.guaranteePercent")}
              </dt>
              <dd className="font-medium text-brand-dark dark:text-gray-100">
                {DEBIT_CREDIT_GUARANTEE_PERCENT}%
              </dd>
            </div>
          </>
        ) : null}
      </dl>
    </div>

    <div className="flex w-fit flex-col gap-2 lg:pl-2">
        <p className="dashboard-section-label mb-3">
          {intl.get("baas.cartoes.design.previewTitle")}
        </p>
        <CardDesignPreview
          backgroundColor={design.backgroundColor}
          cardholderName={design.cardholderPreviewName}
          customLogo={design.customLogoUrl}
          mastercardVariant={design.mastercardVariant}
          network={design.network}
          website={design.website}
          phoneMetropolitan={design.phoneMetropolitan}
          phoneOther={design.phoneOther}
          cardNumberPreview={design.cardNumberPreview}
          expiryPreview={design.expiryPreview}
          cvcPreview={design.cvcPreview}
        />
      </div>
  </div>
  );
};

export const BaasCardProgramCreatePage = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const { can, loading: capabilitiesLoading } = useBaasCapabilities();
  const [stepIndex, setStepIndex] = useState(0);
  const [design, setDesign] = useState({ ...DEFAULT_PRODUCT_DESIGN });
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [pixKeyFundingEnabled, setPixKeyFundingEnabled] = useState(false);
  const [issuancePolicy, setIssuancePolicy] = useState(ISSUANCE_ENTITY_BOTH);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedInstance) {
        setPixKeyFundingEnabled(false);
        setIssuancePolicy(ISSUANCE_ENTITY_BOTH);
        return;
      }
      try {
        const next = await fetchIssuerFunding(selectedInstance);
        if (cancelled) return;
        const enabled = Boolean(next?.enabled);
        setPixKeyFundingEnabled(enabled);
        const policy = next?.cardIssuanceEntityType || ISSUANCE_ENTITY_BOTH;
        setIssuancePolicy(policy);
        setConfig((prev) => {
          let nextConfig = prev;
          if (!enabled && prev.fundingMode === FUNDING_MODE_PIX_KEY) {
            nextConfig = { ...nextConfig, fundingMode: FUNDING_MODE_LINKED_ACCOUNT };
          }
          if (policy === ISSUANCE_ENTITY_PF || policy === ISSUANCE_ENTITY_PJ) {
            nextConfig = { ...nextConfig, issuanceEntityType: policy };
          }
          return nextConfig;
        });
      } catch {
        if (!cancelled) {
          setPixKeyFundingEnabled(false);
          setIssuancePolicy(ISSUANCE_ENTITY_BOTH);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedInstance]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedInstance) {
        setAccounts([]);
        return;
      }
      try {
        const list = await fetchBaasAccounts(selectedInstance, { includeBalances: false });
        if (cancelled) return;
        const rootId = String(selectedInstance.rootAccountId || "").trim();
        const eligible = (Array.isArray(list) ? list : []).filter((account) => {
          const type = String(account?.type || account?.accountType || "")
            .trim()
            .toLowerCase();
          return (
            account?.id === rootId
            || type === "root"
            || type === "operational"
          );
        });
        setAccounts(eligible);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedInstance]);

  const currentStep = CREATE_STEPS[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === CREATE_STEPS.length - 1;

  const validateDesignStep = () => {
    if (!design.name?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.create.validation.programNameRequired"),
      });
      return false;
    }
    return true;
  };

  const validateConfigStep = () => {
    const isCobranded = isCobrandedProgramKind(config.programKind);

    if (isCobranded) {
      const url = config.authorizerClientUrl?.trim();
      if (!url) {
        addToast({
          type: "error",
          title: intl.get("baas.cartoes.create.validation.authorizerUrlRequired"),
        });
        return false;
      }

      try {
        // eslint-disable-next-line no-new
        new URL(url);
      } catch {
        addToast({
          type: "error",
          title: intl.get("baas.cartoes.create.validation.authorizerUrlInvalid"),
        });
        return false;
      }

      if (!config.authorizerPublicKey?.trim()) {
        addToast({
          type: "error",
          title: intl.get("baas.cartoes.create.validation.authorizerPublicKeyRequired"),
        });
        return false;
      }
    }

    if (isMastercardBlackVariant(design) && !(config.invoiceDays?.length > 0)) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.create.validation.invoiceDaysRequired"),
      });
      return false;
    }

    if (
      (config.fundingMode || FUNDING_MODE_LINKED_ACCOUNT) === FUNDING_MODE_LINKED_ACCOUNT
      && !config.fundingAccountId
    ) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.create.validation.fundingAccountRequired"),
      });
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (currentStep === "design" && !validateDesignStep()) {
      return;
    }
    if (currentStep === "config" && !validateConfigStep()) {
      return;
    }
    setStepIndex((current) => Math.min(current + 1, CREATE_STEPS.length - 1));
  };

  const handleBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const handleCreate = async () => {
    if (!validateDesignStep() || !validateConfigStep() || !selectedInstance) {
      return;
    }

    setSaving(true);
    try {
      const program = await createBaasCardProgram(selectedInstance, {
        design: {
          ...design,
          name: design.name.trim(),
        },
        config: {
          ...config,
          programKind: config.programKind || PROGRAM_KIND_NATIVE,
          authorizerClientUrl: config.authorizerClientUrl?.trim() || "",
          authorizerPublicKey: config.authorizerPublicKey?.trim() || "",
          webhookUrl: config.webhookUrl?.trim() || "",
        },
        status: "ACTIVE",
      });

      addToast({
        type: "success",
        title: intl.get("baas.cartoes.create.success"),
      });
      history.push(`/baas/cartoes/${program.id}`);
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          error?.message || intl.get("baas.cartoes.create.submit"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!capabilitiesLoading && !can("issuer.programs.create")) {
    return <Redirect to={CARTOES_LIST_PATH} />;
  }

  return (
    <div className="baas-card-program-create min-w-0 space-y-5">
      <PageHeaderWithBack
        title={intl.get("baas.cartoes.create.pageTitle")}
        subTitle={intl.get("baas.cartoes.create.subtitle")}
        backTo={CARTOES_LIST_PATH}
      />

      <StepTabs activeIndex={stepIndex} onStepClick={setStepIndex} />

      {currentStep === "design" ? (
        <BaasCardProgramDesignForm design={design} onChange={setDesign} />
      ) : null}

      {currentStep === "config" ? (
        <ConfigStep
          design={design}
          config={config}
          onChange={setConfig}
          accounts={accounts}
          pixKeyFundingEnabled={pixKeyFundingEnabled}
          issuancePolicy={issuancePolicy}
        />
      ) : null}

      {currentStep === "review" ? (
        <ReviewStep design={design} config={config} accounts={accounts} />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-brand-dark-border">
        <button
          type="button"
          className="btn-outline inline-flex items-center gap-2 !text-xs"
          onClick={isFirstStep ? () => history.push(CARTOES_LIST_PATH) : handleBack}
        >
          <TbArrowLeft className="h-4 w-4" />
          {isFirstStep
            ? intl.get("baas.cartoes.create.cancel")
            : intl.get("baas.cartoes.create.back")}
        </button>

        {isLastStep ? (
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2 !text-xs"
            onClick={handleCreate}
            disabled={saving}
          >
            <TbCheck className="h-4 w-4" />
            {intl.get("baas.cartoes.create.submit")}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2 !text-xs"
            onClick={handleNext}
          >
            {intl.get("baas.cartoes.create.next")}
            <TbArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
